/**
 * Routes.
 *
 * Three zones with different access:
 *
 *   public    — landing, apply, track. No account needed; this is what borrowers
 *               use, and they never get an account by design.
 *   admin     — the fund's operators.
 *   investor  — read-only views of the loan book and the fund.
 *
 * ## Why the public routes sit outside a lazy boundary
 *
 * Everything that needs Firebase lives under `AuthZone`, which is loaded lazily.
 * The public pages therefore ship without the Auth and Firestore SDKs — see the
 * note in `AuthZone.tsx`. The practical effect is that the page a borrower opens
 * is a fraction of the size of the page an admin opens, which matters because
 * they are the ones on a slow connection.
 *
 * Admin and investor share `AppShell`, which renders a side panel nav per role.
 * role. The guards are in `RequireRole`.
 */

import { Suspense, lazy } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { LandingPage } from '../features/public/LandingPage'
import { PwaInstallPrompt } from '../components/PwaInstallPrompt'
import { Spinner } from '../components/ui'

// Lazy: the apply flow is a large form most visitors to the landing page never
// open, and the tracking page is a separate visit entirely.
const ApplyPage = lazy(() =>
  import('../features/public/ApplyPage').then((m) => ({ default: m.ApplyPage })),
)
const TrackPage = lazy(() =>
  import('../features/public/TrackPage').then((m) => ({ default: m.TrackPage })),
)
const SavingsPlanDetailPage = lazy(() =>
  import('../features/public/SavingsPlanDetailPage').then((m) => ({
    default: m.SavingsPlanDetailPage,
  })),
)
const SavingsPlanApplyPage = lazy(() =>
  import('../features/public/SavingsPlanApplyPage').then((m) => ({
    default: m.SavingsPlanApplyPage,
  })),
)

// Lazy: pulls in Firebase. See AuthZone.tsx.
const AuthZone = lazy(() => import('./AuthZone'))
const AppShell = lazy(() => import('./AppShell').then((m) => ({ default: m.AppShell })))
const SignInPage = lazy(() =>
  import('../features/public/SignInPage').then((m) => ({ default: m.SignInPage })),
)
const RequireRole = lazy(() =>
  import('./RequireRole').then((m) => ({ default: m.RequireRole })),
)
const MembersPage = lazy(() =>
  import('../features/admin/MembersPage').then((m) => ({ default: m.MembersPage })),
)
const SettingsPage = lazy(() =>
  import('../features/admin/SettingsPage').then((m) => ({ default: m.SettingsPage })),
)
const LendingRulesPage = lazy(() =>
  import('../features/admin/LendingRulesPage').then((m) => ({
    default: m.LendingRulesPage,
  })),
)
const LoansPage = lazy(() =>
  import('../features/loans/LoansPage').then((m) => ({ default: m.LoansPage })),
)
// pdf-lib is only pulled in on the loan detail screen, so the ~200 kB it costs is
// never paid by anyone who does not print an agreement.
const NewLoanPage = lazy(() =>
  import('../features/loans/NewLoanPage').then((m) => ({ default: m.NewLoanPage })),
)
const LoanDetailPage = lazy(() =>
  import('../features/loans/LoanDetailPage').then((m) => ({ default: m.LoanDetailPage })),
)
const RecordPaymentPage = lazy(() =>
  import('../features/loans/RecordPaymentPage').then((m) => ({
    default: m.RecordPaymentPage,
  })),
)
const ReturnGoldPage = lazy(() =>
  import('../features/loans/ReturnGoldPage').then((m) => ({ default: m.ReturnGoldPage })),
)
const DashboardPage = lazy(() =>
  import('../features/admin/DashboardPage').then((m) => ({ default: m.DashboardPage })),
)
const InvestorsPage = lazy(() =>
  import('../features/investors/InvestorsPage').then((m) => ({ default: m.InvestorsPage })),
)
const PortfolioPage = lazy(() =>
  import('../features/investors/PortfolioPage').then((m) => ({ default: m.PortfolioPage })),
)
const FundPage = lazy(() =>
  import('../features/investors/FundPage').then((m) => ({ default: m.FundPage })),
)
const InvestorRequestsPage = lazy(() =>
  import('../features/investors/InvestorRequestsPage').then((m) => ({
    default: m.InvestorRequestsPage,
  })),
)
const InvestorLoansPage = lazy(() =>
  import('../features/investors/InvestorLoansPage').then((m) => ({
    default: m.InvestorLoansPage,
  })),
)
const MyLoansPage = lazy(() =>
  import('../features/borrower/MyLoansPage').then((m) => ({ default: m.MyLoansPage })),
)
const SavingsAccountsPage = lazy(() =>
  import('../features/savings/SavingsAccountsPage').then((m) => ({
    default: m.SavingsAccountsPage,
  })),
)
const SavingsAccountDetailPage = lazy(() =>
  import('../features/savings/SavingsAccountDetailPage').then((m) => ({
    default: m.SavingsAccountDetailPage,
  })),
)
const WithdrawalQueuePage = lazy(() =>
  import('../features/savings/WithdrawalQueuePage').then((m) => ({
    default: m.WithdrawalQueuePage,
  })),
)
const MySavingsPage = lazy(() =>
  import('../features/savings/MySavingsPage').then((m) => ({
    default: m.MySavingsPage,
  })),
)
const RequestsPage = lazy(() =>
  import('../features/admin/RequestsPage').then((m) => ({ default: m.RequestsPage })),
)
const RequestDetailPage = lazy(() =>
  import('../features/admin/RequestDetailPage').then((m) => ({
    default: m.RequestDetailPage,
  })),
)
// Lazy for the same reason as the PDF pages: this screen loads SheetJS (~400 kB),
// and it is used once, by one admin, to migrate the old spreadsheet.
const ImportPage = lazy(() =>
  import('../features/import/ImportPage').then((m) => ({ default: m.ImportPage })),
)
const ReportsPage = lazy(() =>
  import('../features/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })),
)
const SavingsPlansSettingsPage = lazy(() =>
  import('../features/admin/SavingsPlansSettingsPage').then((m) => ({
    default: m.SavingsPlansSettingsPage,
  })),
)
const SavingsPlanInterestsPage = lazy(() =>
  import('../features/admin/SavingsPlanInterestsPage').then((m) => ({
    default: m.SavingsPlanInterestsPage,
  })),
)
const QuickRecordPickerPage = lazy(() =>
  import('../features/admin/QuickRecordPickerPage').then((m) => ({
    default: m.QuickRecordPickerPage,
  })),
)
const LandingLayoutSettingsPage = lazy(() =>
  import('../features/admin/LandingLayoutSettingsPage').then((m) => ({
    default: m.LandingLayoutSettingsPage,
  })),
)

// --- the madrasa's exams -------------------------------------------------
// A separate domain sharing the same accounts. Lazy like everything else, and
// the setup page especially so: it carries the ~200 kB question-paper blueprint,
// which nobody marking an exam should have to download.
const ExamHomePage = lazy(() =>
  import('../features/exams/ExamHomePage').then((m) => ({ default: m.ExamHomePage })),
)
const ExamTimetablePage = lazy(() =>
  import('../features/exams/ExamTimetablePage').then((m) => ({ default: m.ExamTimetablePage })),
)
const PaperRosterPage = lazy(() =>
  import('../features/exams/PaperRosterPage').then((m) => ({ default: m.PaperRosterPage })),
)
const PaperEditPage = lazy(() =>
  import('../features/exams/PaperEditPage').then((m) => ({ default: m.PaperEditPage })),
)
const MarkEntryPage = lazy(() =>
  import('../features/exams/MarkEntryPage').then((m) => ({ default: m.MarkEntryPage })),
)
const ExamSetupPage = lazy(() =>
  import('../features/exams/admin/ExamSetupPage').then((m) => ({ default: m.ExamSetupPage })),
)
const ExamStudentsPage = lazy(() =>
  import('../features/exams/admin/StudentsPage').then((m) => ({
    default: m.ExamStudentsPage,
  })),
)
const ExamResultsPage = lazy(() =>
  import('../features/exams/admin/ResultsPage').then((m) => ({ default: m.ExamResultsPage })),
)
const ExaminerReviewPage = lazy(() =>
  import('../features/exams/admin/ExaminerReviewPage').then((m) => ({
    default: m.ExaminerReviewPage,
  })),
)

function Loading() {
  return <Spinner label="Loading…" />
}

export function App() {
  return (
    <BrowserRouter>
      <PwaInstallPrompt />
      <Suspense fallback={<Loading />}>
        <Routes>
          {/* --- public: no Firebase, no account ---------------------------- */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/apply" element={<ApplyPage />} />
          <Route path="/track" element={<TrackPage />} />
          <Route path="/plans/:planId" element={<SavingsPlanDetailPage />} />
          <Route path="/plans/:planId/apply" element={<SavingsPlanApplyPage />} />

          {/* --- everything below needs Firebase --------------------------- */}
          <Route element={<AuthZone />}>
            <Route path="/signin" element={<SignInPage />} />

            {/* --- admin ------------------------------------------------- */}
            <Route
              element={
                <RequireRole allow={['super_admin', 'admin']}>
                  <AppShell />
                </RequireRole>
              }
            >
              <Route path="/admin" element={<DashboardPage />} />
              <Route path="/admin/record/:kind" element={<QuickRecordPickerPage />} />
              <Route path="/admin/requests" element={<RequestsPage />} />
              <Route path="/admin/requests/plans" element={<SavingsPlanInterestsPage />} />
              <Route path="/admin/requests/:requestId" element={<RequestDetailPage />} />
              <Route path="/admin/loans" element={<LoansPage />} />
              <Route path="/admin/loans/new" element={<NewLoanPage />} />
              <Route path="/admin/loans/:loanId" element={<LoanDetailPage />} />
              <Route path="/admin/loans/:loanId/pay" element={<RecordPaymentPage />} />
              <Route path="/admin/loans/:loanId/return-gold" element={<ReturnGoldPage />} />
              <Route path="/admin/investors" element={<InvestorsPage />} />
              <Route path="/admin/savings" element={<SavingsAccountsPage />} />
              {/* The queue is declared before the :accountId route so that
                  "withdrawals" is never mistaken for an account id. */}
              <Route
                path="/admin/savings/withdrawals"
                element={<WithdrawalQueuePage />}
              />
              <Route
                path="/admin/savings/:accountId"
                element={<SavingsAccountDetailPage />}
              />
              <Route path="/admin/import" element={<ImportPage />} />
              <Route path="/admin/reports" element={<ReportsPage />} />
              <Route path="/admin/members" element={<MembersPage />} />
              <Route path="/admin/lending" element={<LendingRulesPage />} />
              <Route path="/admin/settings/savings-plans" element={<SavingsPlansSettingsPage />} />
              <Route path="/admin/settings/landing-layout" element={<LandingLayoutSettingsPage />} />
              <Route
                path="/admin/settings/plan-interests"
                element={<Navigate to="/admin/requests/plans" replace />}
              />
              <Route path="/admin/settings" element={<SettingsPage />} />
              {/* Exam administration: publishing papers, the student roll, and
                  the level mark sheets. Marking itself lives under /exams, which
                  admins reach too — see below. */}
              <Route path="/admin/exams" element={<ExamSetupPage />} />
              <Route path="/admin/exams/students" element={<ExamStudentsPage />} />
              <Route path="/admin/exams/results" element={<ExamResultsPage />} />
              <Route path="/admin/exams/timetable" element={<ExamTimetablePage />} />
              <Route path="/admin/exams/papers/:paperId/review" element={<ExaminerReviewPage />} />
            </Route>

            {/* --- personal loan & savings (when linked) -------------------
                Any approved user may open these when their profile carries
                the link — including investors and admins who also borrow or
                save with the fund. */}
            <Route
              element={
                <RequireRole
                  allow={[
                    'borrower',
                    'saver',
                    'super_admin',
                    'admin',
                    'investor',
                    'teacher',
                  ]}
                >
                  <AppShell />
                </RequireRole>
              }
            >
              <Route path="/my-loans" element={<MyLoansPage />} />
              <Route path="/savings" element={<MySavingsPage />} />
            </Route>

            {/* --- teachers: the exam module and nothing else ------------
                Admins are allowed in as well, because during exam week an admin
                is usually also an examiner, and making them sign in as somebody
                else to mark a student would be absurd. */}
            <Route
              element={
                <RequireRole allow={['teacher', 'super_admin', 'admin']}>
                  <AppShell />
                </RequireRole>
              }
            >
              <Route path="/exams" element={<ExamHomePage />} />
              <Route path="/exams/timetable" element={<ExamTimetablePage />} />
              <Route path="/exams/:paperId" element={<PaperRosterPage />} />
              {/* Editing the questions. Reachable by teachers as well as admins —
                  the L4/L5 recitation papers ship with blanks the panel fills in. */}
              <Route path="/exams/:paperId/edit" element={<PaperEditPage />} />
              <Route path="/exams/:paperId/:studentId" element={<MarkEntryPage />} />
            </Route>

            {/* --- investor ---------------------------------------------- */}
            <Route
              element={
                <RequireRole allow={['investor', 'super_admin', 'admin']}>
                  <AppShell />
                </RequireRole>
              }
            >
              <Route path="/portfolio" element={<PortfolioPage />} />
              <Route path="/loans" element={<InvestorLoansPage />} />
              <Route path="/requests" element={<InvestorRequestsPage />} />
              <Route path="/fund" element={<FundPage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
